/*
  Store locator.

  The dataset is one spreadsheet uploaded to the "Store locator" metaobject. Liquid passes
  its URL down as data-file-url; the workbook is fetched, parsed and rendered here so the
  merchant can swap the file without touching the theme.

  The client's export only carries Name / Strasse / Ort, so the addresses are geocoded in
  the browser (Photon, OpenStreetMap data) and the answers are kept in localStorage: the
  list renders straight away from the file, the map pins fill in behind it, and a repeat
  visit costs no requests at all. A file that already has Latitude / Longitude columns
  skips geocoding entirely.

  Map: Leaflet on OpenStreetMap tiles - no API key, no billing, no request quota.
*/

const STORE_LOCATOR_LIBS = {
  leafletCss: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css',
  leafletJs: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js',
  clusterCss: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.min.css',
  clusterJs: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.min.js',
  sheetJs: 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js',
};

/*
  Two geocoders rather than one: both are free community services that throttle by IP, and
  a visitor who just loaded a few hundred lookups can find the first one closed for a while.
  The second one then still answers the one query that matters, the visitor's own.
*/
const STORE_LOCATOR_GEOCODERS = [
  {
    url: (query) => `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=1&lang=de`,
    parse: (data) => {
      const feature = data.features && data.features[0];
      if (!feature) return null;

      const [longitude, latitude] = feature.geometry.coordinates;
      const properties = feature.properties;
      const town = properties.city || properties.town || properties.village || properties.county;

      return {
        latitude,
        longitude,
        zip: properties.postcode || '',
        label: [properties.postcode, town || properties.name].filter(Boolean).join(' '),
      };
    },
  },
  {
    url: (query) =>
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}` +
      '&format=jsonv2&limit=1&addressdetails=1&accept-language=de',
    parse: (data) => {
      const place = Array.isArray(data) && data[0];
      if (!place) return null;

      const address = place.address || {};
      const town = address.city || address.town || address.village || address.municipality;

      return {
        latitude: Number.parseFloat(place.lat),
        longitude: Number.parseFloat(place.lon),
        zip: address.postcode || '',
        label: [address.postcode, town || place.name].filter(Boolean).join(' '),
      };
    },
  },
];
const STORE_LOCATOR_CACHE_KEY = 'store-locator-geocode-v1';
const STORE_LOCATOR_CONCURRENCY = 4;
const STORE_LOCATOR_DEFAULT_COUNTRY = 'Deutschland';

const STORE_LOCATOR_COLUMNS = {
  name: ['name', 'markt', 'marktname', 'filiale', 'store', 'verkaufsstelle', 'kunde'],
  street: ['strasse', 'street', 'adresse', 'address', 'anschrift'],
  zip: ['plz', 'postleitzahl', 'zip', 'postcode', 'postal code'],
  city: ['ort', 'stadt', 'city', 'town'],
  country: ['land', 'country'],
  latitude: ['latitude', 'lat', 'breitengrad'],
  longitude: ['longitude', 'lng', 'lon', 'long', 'laengengrad'],
};

const storeLocatorAssets = new Map();

function loadStoreLocatorScript(src) {
  if (storeLocatorAssets.has(src)) return storeLocatorAssets.get(src);

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
    document.head.appendChild(script);
  });

  storeLocatorAssets.set(src, promise);
  return promise;
}

function loadStoreLocatorStylesheet(href) {
  if (storeLocatorAssets.has(href)) return storeLocatorAssets.get(href);

  const promise = new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    // A missing stylesheet must not block the map, so this resolves either way.
    link.addEventListener('load', () => resolve());
    link.addEventListener('error', () => resolve());
    document.head.appendChild(link);
  });

  storeLocatorAssets.set(href, promise);
  return promise;
}

/* Folds the German umlauts and casing so "Straße", "STRASSE" and "strasse" all match. */
function normalizeStoreLocatorText(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function toStoreLocatorNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const parsed = Number.parseFloat(String(value == null ? '' : value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function storeLocatorDistance(from, to) {
  const earthRadius = 6371;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const deltaLat = toRadians(to.latitude - from.latitude);
  const deltaLng = toRadians(to.longitude - from.longitude);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* Private to the visitor's browser, so a wiped or blocked store only costs a re-geocode. */
function readStoreLocatorCache() {
  try {
    return JSON.parse(window.localStorage.getItem(STORE_LOCATOR_CACHE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

function writeStoreLocatorCache(cache) {
  try {
    window.localStorage.setItem(STORE_LOCATOR_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    /* Private mode and full quotas both land here; geocoding still works, just uncached. */
  }
}

/*
  Answers carry the place the geocoder settled on, which is what lets the section echo
  "40878" back as "40878 Ratingen". Throws only when no geocoder could be reached at all,
  so an unreachable service can be told apart from an address that simply does not exist.
*/
async function geocodeStoreLocatorQuery(query) {
  let reached = false;

  for (const geocoder of STORE_LOCATOR_GEOCODERS) {
    try {
      const response = await fetch(geocoder.url(query));
      if (!response.ok) continue;

      reached = true;

      const found = geocoder.parse(await response.json());
      if (found) return found;
    } catch (error) {
      console.error('[store-locator]', error);
    }
  }

  if (!reached) throw new Error('No geocoder could be reached');

  return null;
}

class StoreLocator extends HTMLElement {
  constructor() {
    super();

    this.onSubmit = this.handleSubmit.bind(this);
    this.onGeolocate = this.handleGeolocate.bind(this);
    this.onFilterChange = this.handleFilterChange.bind(this);
    this.onListClick = this.handleListClick.bind(this);
    this.onListKeydown = this.handleListKeydown.bind(this);
    this.onIntersect = this.handleIntersect.bind(this);

    this.stores = [];
    this.results = [];
    this.markers = [];
    this.origin = null;
    this.originMarker = null;
    this.geocoderFailed = false;
    this.map = null;
    this.cluster = null;
    this.started = false;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-search-form]');
    this.queryInput = this.querySelector('[data-query]');
    this.geolocateButton = this.querySelector('[data-geolocate]');
    this.radiusSelect = this.querySelector('[data-radius]');
    this.limitSelect = this.querySelector('[data-limit]');
    this.statusElement = this.querySelector('[data-status]');
    this.countElement = this.querySelector('[data-count]');
    this.scopeElement = this.querySelector('[data-scope]');
    this.submitButton = this.querySelector('[data-submit]');
    this.listElement = this.querySelector('[data-list]');
    this.mapElement = this.querySelector('[data-map]');

    this.numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      maximumFractionDigits: 1,
    });

    this.form.addEventListener('submit', this.onSubmit);
    this.geolocateButton.addEventListener('click', this.onGeolocate);
    this.radiusSelect.addEventListener('change', this.onFilterChange);
    this.limitSelect.addEventListener('change', this.onFilterChange);
    this.listElement.addEventListener('click', this.onListClick);
    this.listElement.addEventListener('keydown', this.onListKeydown);

    // Leaflet, the cluster plugin and the spreadsheet parser are only worth their weight
    // once the section is actually approached.
    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '400px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.onSubmit);
    this.geolocateButton.removeEventListener('click', this.onGeolocate);
    this.radiusSelect.removeEventListener('change', this.onFilterChange);
    this.limitSelect.removeEventListener('change', this.onFilterChange);
    this.listElement.removeEventListener('click', this.onListClick);
    this.listElement.removeEventListener('keydown', this.onListKeydown);
    if (this.observer) this.observer.disconnect();
    if (this.map) this.map.remove();
  }

  handleIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting) || this.started) return;

    this.started = true;
    this.observer.disconnect();
    this.start();
  }

  /* Disables the button and swaps in the spinner while the geocoder is being asked. */
  setBusy(isBusy) {
    if (!this.submitButton) return;

    this.submitButton.toggleAttribute('data-busy', isBusy);
    this.submitButton.disabled = isBusy;
  }

  setStatus(message, isError) {
    this.statusElement.textContent = message || '';
    this.statusElement.toggleAttribute('data-show', Boolean(message));
    this.statusElement.toggleAttribute('data-error', Boolean(isError));
  }

  async start() {
    const fileUrl = this.dataset.fileUrl;

    this.setStatus(fileUrl ? this.dataset.loadingText : this.dataset.errorText, !fileUrl);

    // The map is worth showing even when the dataset is missing or unreadable, so it is
    // built before the workbook is looked at and never blocked by it.
    try {
      await this.buildMap();
    } catch (error) {
      console.error('[store-locator]', error);
    }

    if (!fileUrl) return;

    try {
      this.stores = await this.fetchStores(fileUrl);
    } catch (error) {
      console.error('[store-locator]', error);
      this.setStatus(this.dataset.errorText, true);
      return;
    }

    // The list needs no coordinates, so it is on screen before geocoding even starts.
    this.render();
    await this.geocodeStores();
    this.setStatus('', false);
    this.render();
  }

  async fetchStores(fileUrl) {
    await loadStoreLocatorScript(STORE_LOCATOR_LIBS.sheetJs);

    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error(`Store data request failed with ${response.status}`);

    const workbook = window.XLSX.read(await response.arrayBuffer(), { type: 'array' });

    /*
      Every sheet is read, not just the first one that happens to carry addresses: the
      export ships the same markets on several sheets - once per market on "HV pro Markt",
      once per market and article on "HV_Wochenverlauf" - and the sheet order is not
      something the file guarantees. Collecting all of them and keying by address makes the
      result the same whichever sheet comes first, and collapses the repeats.
    */
    const byAddress = new Map();

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) return;

      this.parseSheet(sheet).forEach((store) => {
        const key = normalizeStoreLocatorText(`${store.name}|${store.street}|${store.city}`);
        const known = byAddress.get(key);

        // A later sheet may fill in a column an earlier one left empty.
        if (known) {
          known.zip = known.zip || store.zip;
          known.latitude = known.latitude === null ? store.latitude : known.latitude;
          known.longitude = known.longitude === null ? store.longitude : known.longitude;
          return;
        }

        byAddress.set(key, store);
      });
    });

    if (!byAddress.size) throw new Error('No sheet with address columns was found');

    return [...byAddress.values()];
  }

  /*
    The client's export is a report, not a clean table: it opens with a title row, a couple
    of blank ones and a "Gesamtergebnis" summary, and its store-name column carries no
    header at all. So the header row is located by content rather than assumed to be row 1.
  */
  parseSheet(sheet) {
    const rows = window.XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });

    let headerIndex = -1;
    let headers = [];

    for (let index = 0; index < Math.min(rows.length, 40); index += 1) {
      const cells = rows[index].map((cell) => normalizeStoreLocatorText(cell));
      const hasStreet = cells.some((cell) => STORE_LOCATOR_COLUMNS.street.includes(cell));
      const hasCity = cells.some((cell) => STORE_LOCATOR_COLUMNS.city.includes(cell));

      if (hasStreet && hasCity) {
        headerIndex = index;
        headers = cells;
        break;
      }
    }

    if (headerIndex === -1) return [];

    const columns = {};
    Object.keys(STORE_LOCATOR_COLUMNS).forEach((field) => {
      columns[field] = headers.findIndex((header) => STORE_LOCATOR_COLUMNS[field].includes(header));
    });

    // No "Name" header in the report: the store name sits in the unlabelled column closest
    // to the left of the street.
    if (columns.name === -1 && columns.street > 0) {
      for (let index = columns.street - 1; index >= 0; index -= 1) {
        if (!headers[index]) {
          columns.name = index;
          break;
        }
      }
    }

    const read = (row, field) => {
      if (columns[field] === -1) return '';
      return String(row[columns[field]] == null ? '' : row[columns[field]]).trim();
    };

    const stores = [];

    rows.slice(headerIndex + 1).forEach((row) => {
      const name = read(row, 'name');
      const street = read(row, 'street');
      const city = read(row, 'city');

      // Summary and spacer rows carry a label but no address, which is what filters them out.
      if (!name || !street || !city) return;

      stores.push({
        name,
        street,
        city,
        zip: read(row, 'zip'),
        country: read(row, 'country') || STORE_LOCATOR_DEFAULT_COUNTRY,
        latitude: toStoreLocatorNumber(read(row, 'latitude')),
        longitude: toStoreLocatorNumber(read(row, 'longitude')),
      });
    });

    return stores;
  }

  /*
    Geocodes every store the file did not already carry coordinates for. Answers are cached
    per address, so this is paid once per visitor and not at all when the file gains
    Latitude / Longitude columns later.
  */
  async geocodeStores() {
    const cache = readStoreLocatorCache();
    const queue = [];

    this.stores.forEach((store) => {
      if (store.latitude !== null && store.longitude !== null) return;

      const key = normalizeStoreLocatorText(`${store.street}|${store.zip}|${store.city}`);
      const cached = cache[key];

      if (cached) {
        store.latitude = cached.latitude;
        store.longitude = cached.longitude;
        store.zip = store.zip || cached.zip || '';
        return;
      }

      queue.push({ store, key });
    });

    if (!queue.length) return;

    let cursor = 0;
    let sinceRender = 0;

    const worker = async () => {
      while (cursor < queue.length) {
        const { store, key } = queue[cursor];
        cursor += 1;

        const found = await this.geocodeStore(store);

        if (found) {
          store.latitude = found.latitude;
          store.longitude = found.longitude;
          store.zip = store.zip || found.zip || '';
          cache[key] = found;
        }

        sinceRender += 1;
        // Pins appear in batches while the rest is still being looked up.
        if (sinceRender >= STORE_LOCATOR_CONCURRENCY * 2) {
          sinceRender = 0;
          this.render();
        }
      }
    };

    await Promise.all(Array.from({ length: STORE_LOCATOR_CONCURRENCY }, worker));
    writeStoreLocatorCache(cache);
  }

  async geocodeStore(store) {
    const attempts = [
      [store.street, store.zip, store.city, store.country].filter(Boolean).join(', '),
      [store.city, store.country].filter(Boolean).join(', '),
    ];

    for (const query of attempts) {
      try {
        const found = await geocodeStoreLocatorQuery(query);
        if (found) return found;
      } catch (error) {
        console.error('[store-locator]', error);
      }
    }

    return null;
  }

  async handleSubmit(event) {
    event.preventDefault();

    const query = String(this.queryInput.value || '').trim();

    this.setBusy(true);
    this.setStatus(query ? this.dataset.searchingText : '', false);

    try {
      this.origin = await this.resolveOrigin(query);
    } finally {
      this.setBusy(false);
    }

    // A typed location that resolves to nothing is its own message: without it the visitor
    // only sees the unchanged full list and cannot tell the search ran at all. An
    // unreachable geocoder says so separately - the address may be perfectly fine.
    if (query && !this.origin) {
      this.render();
      this.setStatus(
        this.geocoderFailed ? this.dataset.geocoderErrorText : this.dataset.locationErrorText,
        true
      );
      return;
    }

    this.setStatus('', false);
    this.render();
    this.listElement.scrollTop = 0;
  }

  handleFilterChange() {
    this.render();
  }

  /*
    The typed value is geocoded, which is what makes a plain PLZ or a street address work
    even when no store sits in that town. If the geocoder is unreachable, the value is
    matched against the dataset instead so the search still does something useful.
  */
  async resolveOrigin(rawQuery) {
    const query = String(rawQuery || '').trim();
    this.geocoderFailed = false;
    if (!query) return null;

    try {
      const found = await geocodeStoreLocatorQuery(`${query}, ${STORE_LOCATOR_DEFAULT_COUNTRY}`);
      if (found) {
        return { latitude: found.latitude, longitude: found.longitude, label: found.label || query };
      }
    } catch (error) {
      this.geocoderFailed = true;
      console.error('[store-locator]', error);
    }

    const normalized = normalizeStoreLocatorText(query);
    const match = this.stores.find(
      (store) =>
        store.latitude !== null &&
        (normalizeStoreLocatorText(store.zip).startsWith(normalized) ||
          normalizeStoreLocatorText(store.city).includes(normalized) ||
          normalizeStoreLocatorText(store.name).includes(normalized))
    );

    return match
      ? { latitude: match.latitude, longitude: match.longitude, label: match.city || query }
      : null;
  }

  handleGeolocate() {
    if (!navigator.geolocation) {
      this.setStatus(this.dataset.geolocationErrorText, true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.origin = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          label: this.dataset.originLabel,
        };
        this.queryInput.value = '';
        this.setStatus('', false);
        this.render();
      },
      () => this.setStatus(this.dataset.geolocationErrorText, true)
    );
  }

  buildResults() {
    const limit = Number.parseInt(this.limitSelect.value, 10) || this.stores.length;

    if (!this.origin) {
      return [...this.stores].sort((a, b) => a.name.localeCompare(b.name, 'de')).slice(0, limit);
    }

    const radius = Number.parseFloat(this.radiusSelect.value) || Infinity;

    return this.stores
      .filter((store) => store.latitude !== null && store.longitude !== null)
      .map((store) => ({ ...store, distance: storeLocatorDistance(this.origin, store) }))
      .filter((store) => store.distance <= radius)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, limit);
  }

  render() {
    this.results = this.buildResults();
    this.renderList();
    this.renderMarkers();

    // A typed query that matches nothing, and an origin with an empty radius, both land here.
    const noMatch = this.stores.length > 0 && this.results.length === 0;
    if (noMatch) {
      this.setStatus(this.dataset.emptyText, false);
    } else if (this.statusElement.textContent === this.dataset.emptyText) {
      this.setStatus('', false);
    }
  }

  renderList() {
    const countLabel = this.dataset.resultsCountLabel;
    this.countElement.textContent = this.results.length
      ? `${this.results.length} ${countLabel}`.trim()
      : '';

    this.renderScope();

    const fragment = document.createDocumentFragment();

    this.results.forEach((store, index) => {
      const item = document.createElement('li');
      item.className = 'store-locator__item';
      item.dataset.index = String(index);
      item.tabIndex = 0;

      const name = document.createElement('p');
      name.className = 'store-locator__name';
      name.textContent = store.name;
      item.appendChild(name);

      const address = document.createElement('p');
      address.className = 'store-locator__address';
      address.textContent = this.formatAddress(store);
      item.appendChild(address);

      const meta = document.createElement('div');
      meta.className = 'store-locator__meta';

      if (typeof store.distance === 'number') {
        const distance = document.createElement('span');
        distance.className = 'store-locator__distance';
        distance.textContent = `${this.numberFormat.format(store.distance)} ${
          this.dataset.radiusUnit
        } ${this.dataset.distanceLabel}`.trim();
        meta.appendChild(distance);
      }

      const directions = document.createElement('a');
      directions.className = 'store-locator__directions';
      directions.href = this.directionsUrl(store);
      directions.target = '_blank';
      directions.rel = 'noopener';
      directions.textContent = this.dataset.directionsLabel;
      meta.appendChild(directions);

      item.appendChild(meta);
      fragment.appendChild(item);
    });

    this.listElement.replaceChildren(fragment);
  }

  /*
    The count alone reads the same before and after a search, so the line under it spells
    out the radius and the place the geocoder settled on.
  */
  renderScope() {
    if (!this.scopeElement) return;

    if (!this.origin) {
      this.scopeElement.textContent = this.dataset.allLabel || '';
      return;
    }

    const radius = `${this.radiusSelect.value} ${this.dataset.radiusUnit}`.trim();
    this.scopeElement.textContent = (this.dataset.scopeLabel || '')
      .replace('[radius]', radius)
      .replace('[ort]', this.origin.label || '');
  }

  formatAddress(store) {
    const locality = [store.zip, store.city].filter(Boolean).join(' ');
    return [store.street, locality, store.country].filter(Boolean).join(', ');
  }

  directionsUrl(store) {
    const destination = encodeURIComponent(this.formatAddress(store) || store.name);
    const origin = this.origin
      ? `&origin=${encodeURIComponent(`${this.origin.latitude},${this.origin.longitude}`)}`
      : '';

    return `https://www.google.com/maps/dir/?api=1&destination=${destination}${origin}&travelmode=driving`;
  }

  renderMarkers() {
    if (!this.map) return;

    const leaflet = window.L;
    this.cluster.clearLayers();
    this.markers = [];

    const pin = leaflet.divIcon({
      className: '',
      iconSize: [26, 34],
      iconAnchor: [13, 34],
      popupAnchor: [0, -30],
      html:
        '<svg class="store-locator__pin" xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34" fill="none" aria-hidden="true">' +
        '<path d="M13 33s11-12.2 11-20A11 11 0 1 0 2 13c0 7.8 11 20 11 20z" fill="currentColor"/>' +
        '<circle cx="13" cy="12.6" r="4.2" fill="#ffffff"/></svg>',
    });

    this.renderOrigin();

    // A store still waiting for its coordinates stays in the list but has no pin yet.
    const located = this.results.filter(
      (store) => store.latitude !== null && store.longitude !== null
    );

    located.forEach((store) => {
      const marker = leaflet.marker([store.latitude, store.longitude], { icon: pin });
      const popup = document.createElement('div');
      const popupName = document.createElement('span');
      popupName.className = 'store-locator__popup-name';
      popupName.textContent = store.name;
      popup.appendChild(popupName);
      popup.appendChild(document.createTextNode(this.formatAddress(store)));
      marker.bindPopup(popup);

      this.markers[this.results.indexOf(store)] = marker;
      this.cluster.addLayer(marker);
    });

    if (!located.length) {
      // Nothing in range: the map still moves to the searched place instead of sitting on
      // the previous result, which would look like the search did nothing.
      if (this.origin) this.map.setView([this.origin.latitude, this.origin.longitude], 9);
      return;
    }

    const bounds = leaflet.latLngBounds(located.map((store) => [store.latitude, store.longitude]));
    if (this.origin) bounds.extend([this.origin.latitude, this.origin.longitude]);
    this.map.fitBounds(bounds, { padding: [32, 32], maxZoom: 13 });
  }

  /*
    The searched location gets a marker of its own, outside the cluster: the visitor can
    see where the search started and how the results sit around it.
  */
  renderOrigin() {
    const leaflet = window.L;

    if (this.originMarker) {
      this.map.removeLayer(this.originMarker);
      this.originMarker = null;
    }

    if (!this.origin) return;

    this.originMarker = leaflet
      .marker([this.origin.latitude, this.origin.longitude], {
        keyboard: false,
        zIndexOffset: 1000,
        icon: leaflet.divIcon({
          className: '',
          iconSize: [22, 22],
          iconAnchor: [11, 11],
          html: '<span class="store-locator__origin"></span>',
        }),
      })
      .addTo(this.map);

    const label = this.origin.label || this.dataset.originLabel;
    if (label) this.originMarker.bindTooltip(label, { direction: 'top', offset: [0, -12] });
  }

  async buildMap() {
    await Promise.all([
      loadStoreLocatorStylesheet(STORE_LOCATOR_LIBS.leafletCss),
      loadStoreLocatorStylesheet(STORE_LOCATOR_LIBS.clusterCss),
    ]);
    await loadStoreLocatorScript(STORE_LOCATOR_LIBS.leafletJs);
    await loadStoreLocatorScript(STORE_LOCATOR_LIBS.clusterJs);

    const leaflet = window.L;

    this.map = leaflet.map(this.mapElement, { scrollWheelZoom: false }).setView([51.3, 10.2], 5);

    leaflet
      .tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      })
      .addTo(this.map);

    this.cluster = leaflet.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 48,
      iconCreateFunction: (cluster) =>
        leaflet.divIcon({
          html: `<span class="store-locator__cluster">${cluster.getChildCount()}</span>`,
          className: '',
          iconSize: [40, 40],
        }),
    });

    this.map.addLayer(this.cluster);
  }

  handleListClick(event) {
    const item = event.target instanceof Element ? event.target.closest('.store-locator__item') : null;
    if (!item || event.target.closest('a')) return;

    this.activate(item);
  }

  handleListKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const item = event.target instanceof Element ? event.target.closest('.store-locator__item') : null;
    if (!item) return;

    event.preventDefault();
    this.activate(item);
  }

  activate(item) {
    const index = Number.parseInt(item.dataset.index, 10);
    const marker = this.markers[index];

    this.listElement.querySelectorAll('[data-active]').forEach((active) => {
      active.removeAttribute('data-active');
    });
    item.setAttribute('data-active', '');

    if (!marker) return;

    // Inside a cluster the marker has no position of its own until the cluster is opened.
    this.cluster.zoomToShowLayer(marker, () => marker.openPopup());
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
